import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, MapPin, ArrowRight, Phone } from "lucide-react";
import { SERVICE_AREAS, SITE } from "../seo";
import { COMPANY, ASSETS } from "../mock";
import { SectionHeading } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

export default function ServiceAreas() {
  const navigate = useNavigate();
  useScrollReveal();
  useSEO({
    title: "Service Areas - Screen & Pool Enclosures in Brevard County, FL | Miller & Miller",
    description: "Miller & Miller Services LLC builds pool cages, screen enclosures & patios across Brevard County, FL - Melbourne, Palm Bay, Viera, Rockledge, Merritt Island, Titusville & more.",
    keywords: ["screen enclosure Brevard County", "pool cage Melbourne FL", "pool enclosure Palm Bay", "screen enclosure Viera"],
    path: "/service-areas",
  });

  return (
    <div>
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={ASSETS.pool1} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">Service Areas</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">Proudly Serving All of Brevard County, FL</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">As a local, family-owned team based in {SITE.primaryCity}, we design, build, and service screen enclosures throughout the Space Coast. Find your city below.</p>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x">
          <SectionHeading center eyebrow="Where We Work" title="Space Coast Cities We Serve" />
          <div className="mt-8 md:mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_AREAS.map((c, i) => (
              <Link key={c.slug} to={`/service-areas/${c.slug}`} className="reveal group rounded-2xl bg-white p-6 ring-1 ring-brand-mist hover:ring-brand-sky/40 hover:shadow-card transition-all" style={{ transitionDelay: `${(i % 3) * 60}ms` }}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-mist text-brand-ocean group-hover:bg-brand-ocean group-hover:text-white transition-colors"><MapPin className="h-6 w-6" /></span>
                  <h2 className="text-lg font-heading font-extrabold text-brand-navy">{c.name}, FL</h2>
                </div>
                <p className="mt-3 text-sm text-brand-slate leading-relaxed line-clamp-3">{c.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-heading font-bold text-brand-ocean group-hover:gap-2.5 transition-all">
                  View {c.name} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-white">Don't see your city?</h2>
          <p className="text-white/90 mt-3">Give us a call - we serve homeowners throughout Brevard County and beyond.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
